import {
  bandOf,
  LEGACY_RISK_MATRIX,
  RA_ACCEPTANCES,
  RA_ACCEPTORS,
  RA_BAND_KEYS,
  type RaAcceptance,
  type RaAcceptor,
  type RaBandKey,
  type RaMatrixDefinition,
  raMatrixIssues,
} from '@felix/domain';
import {
  ActionIcon,
  Alert,
  Button,
  MultiSelect,
  NumberInput,
  Select,
  Skeleton,
  Textarea,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconLayoutGrid,
  IconPlus,
  IconRefresh,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import { errorCode } from '../personnel/source.js';
import riskClasses from '../risk/Risk.module.css';
import { LevelBadge, useRiskError } from '../risk/RiskParts.js';
import type { RiskMatrixSettings } from '../risk/types.js';
import classes from './RiskMatrixSettings.module.css';
import { addBand, removeBand, sameDefinition, withBand, withMax } from './risk-matrix-model.js';
import { CardFooter, settingsClasses } from './SettingsLayout.js';

export interface RiskMatrixSettingsSource {
  load(): Promise<RiskMatrixSettings>;
  /** Yoksa salt okunur (gemi ya da "Ayarlar / düzenleme" yetkisi yok). */
  select?: (rowVersion: number | null, profileId: string) => Promise<RiskMatrixSettings>;
  publish?: (
    profileId: string,
    expectedVersion: number,
    definition: RaMatrixDefinition,
    reason: string,
  ) => Promise<RiskMatrixSettings>;
}

export interface RiskMatrixSettingsPanelProps {
  /** `null`: okunuyor ya da okunamadı. */
  settings: RiskMatrixSettings | null;
  error?: boolean;
  onRetry?: () => void;
  onSelect?: (rowVersion: number | null, profileId: string) => Promise<void>;
  onPublish?: (
    profileId: string,
    expectedVersion: number,
    definition: RaMatrixDefinition,
    reason: string,
  ) => Promise<void>;
}

const SCALE = [1, 2, 3, 4, 5];

/** 5×5 önizleme: her hücre skor ve bandın rengi (ADR 0085 §2). */
export function MatrixPreview({ definition }: { definition: RaMatrixDefinition }) {
  const { t } = useTranslation();
  const valid = raMatrixIssues(definition).length === 0;
  return (
    <div className={classes.preview}>
      <table className={classes.previewTable}>
        <caption className={classes.axes}>
          {t('risk.matrix.likelihood')} × {t('risk.matrix.impact')}
        </caption>
        <tbody>
          {[5, 4, 3, 2, 1].map((l) => (
            <tr key={`r${l}`}>
              <th scope="row">{l}</th>
              {SCALE.map((s) => {
                const score = l * s;
                const level: RaBandKey | null = valid ? bandOf(definition, score).key : null;
                return (
                  <td
                    key={`${l}-${s}`}
                    className={classes.cell}
                    data-level={level ?? undefined}
                    aria-label={t('risk.matrixProfile.cell', {
                      likelihood: l,
                      severity: s,
                      score,
                      level: level ? t(`risk.levels.${level}`) : '—',
                    })}
                  >
                    {score}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td />
            {SCALE.map((s) => (
              <th key={`c${s}`} scope="col">
                {s}
              </th>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/**
 * Settings ▸ Risk Matrix (K1; ADR 0085 §2): şirketin profilleri, geçerli profilin bantları ve
 * kabul seviyeleri, 5×5 önizleme ve sürüm geçmişi. Ofiste profil seçilir ve yeni sürüm yayımlanır
 * (gerekçeyle; eski sürüm silinmez). Gemide salt okunur; şirket verisi olarak iner.
 */
export function RiskMatrixSettingsPanel({
  settings,
  error,
  onRetry,
  onSelect,
  onPublish,
}: RiskMatrixSettingsPanelProps) {
  const { t } = useTranslation();
  const message = useRiskError();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`risk.matrixProfile.${key}`, options ?? {});
  const [viewed, setViewed] = useState<string | null>(null);
  const [draft, setDraft] = useState<RaMatrixDefinition | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  const inUse = settings?.current?.profileId ?? settings?.profiles[0]?.id ?? null;
  const profile =
    settings?.profiles.find((p) => p.id === (viewed ?? inUse)) ?? settings?.profiles[0] ?? null;
  const latest = profile?.versions.at(-1) ?? null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: profil ya da sürüm değişince taslak sıfırlanır
  useEffect(() => {
    setDraft(latest ? latest.definition : null);
    setReason('');
  }, [profile?.id, latest?.id]);
  // Profil yoksa (gemiye henüz inmedi) geçerli olan Legacy 5-band gösterilir (ADR 0085 §1)
  const legacyOnly = settings !== null && settings.profiles.length === 0;
  const definition = draft ?? latest?.definition ?? (legacyOnly ? LEGACY_RISK_MATRIX : null);
  const issues = useMemo(() => (definition ? raMatrixIssues(definition) : []), [definition]);
  const editable = onPublish !== undefined;

  if (error) {
    return (
      <Panel title={k('title')} icon={<IconLayoutGrid size={18} stroke={1.8} />} divider>
        <EmptyState
          tone="danger"
          title={t('common.loadError')}
          action={
            onRetry ? (
              <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={onRetry}>
                {k('retry')}
              </Button>
            ) : undefined
          }
        />
      </Panel>
    );
  }
  if (!settings) {
    return (
      <Panel title={k('title')} icon={<IconLayoutGrid size={18} stroke={1.8} />} divider>
        <div aria-busy="true">
          <Skeleton height={18} mb={10} />
          <Skeleton height={120} />
        </div>
      </Panel>
    );
  }

  const changed = definition && latest ? !sameDefinition(definition, latest.definition) : false;
  const run = async (action: () => Promise<void>, success: string) => {
    setBusy(true);
    setNotice(null);
    try {
      await action();
      setNotice({ tone: 'success', text: success });
    } catch (e) {
      setNotice({ tone: 'danger', text: message(errorCode(e)) });
    } finally {
      setBusy(false);
    }
  };
  const acceptorLabel = (roles: readonly RaAcceptor[]) =>
    roles.length === 0 ? '—' : roles.map((r) => k(`acceptors.${r}`)).join(', ');

  return (
    <Panel title={k('title')} icon={<IconLayoutGrid size={18} stroke={1.8} />} divider>
      <p className={settingsClasses.intro}>{k('intro')}</p>
      {editable ? null : <p className={settingsClasses.note}>{k('shipNote')}</p>}
      <div className={classes.head}>
        {legacyOnly ? (
          <>
            <span>{k('legacy')}</span>
            <StatusBadge tone="success" withIcon={false}>
              {k('inUse')}
            </StatusBadge>
          </>
        ) : null}
        <Select
          display={legacyOnly ? 'none' : undefined}
          label={k('profile')}
          className={classes.profile}
          allowDeselect={false}
          value={profile?.id ?? null}
          data={settings.profiles.map((p) => ({
            value: p.id,
            label: k('versionLabel', { name: p.name, version: p.versions.at(-1)?.version ?? 1 }),
          }))}
          onChange={(value) => value && setViewed(value)}
        />
        {profile && profile.id === inUse ? (
          <StatusBadge tone="success" withIcon={false}>
            {k('inUse')}
          </StatusBadge>
        ) : null}
        {profile && profile.id !== inUse && onSelect ? (
          <Button
            variant="default"
            loading={busy}
            onClick={() => void run(() => onSelect(settings.rowVersion, profile.id), k('selected'))}
          >
            {k('use')}
          </Button>
        ) : null}
      </div>

      {definition ? (
        <div className={classes.body} data-edit={editable || undefined}>
          <section
            className={classes.bands}
            aria-label={k('bands')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={`${riskClasses.table} ${classes.bandTable}`}>
              <thead>
                <tr>
                  <th scope="col">{k('band')}</th>
                  <th scope="col">{k('score')}</th>
                  <th scope="col">{k('acceptance')}</th>
                  <th scope="col">{k('acceptedBy')}</th>
                  {editable ? <th scope="col" aria-label={k('removeBand', { n: '' })} /> : null}
                </tr>
              </thead>
              <tbody>
                {definition.bands.map((band, index) => {
                  const n = index + 1;
                  const last = index === definition.bands.length - 1;
                  return (
                    <tr key={`${band.key}-${band.min}`}>
                      <td>
                        {editable ? (
                          <Select
                            size="xs"
                            className={classes.keyField}
                            aria-label={`${k('band')} ${n}`}
                            allowDeselect={false}
                            value={band.key}
                            data={RA_BAND_KEYS.map((key) => ({
                              value: key,
                              label: t(`risk.levels.${key}`),
                            }))}
                            onChange={(v) =>
                              v && setDraft(withBand(definition, index, { key: v as RaBandKey }))
                            }
                          />
                        ) : (
                          <LevelBadge level={band.key} />
                        )}
                      </td>
                      <td className={classes.range}>
                        {editable && !last ? (
                          <span className={classes.rangeEdit}>
                            <span>{band.min}–</span>
                            <NumberInput
                              size="xs"
                              aria-label={`${k('to')} ${n}`}
                              min={band.min}
                              max={24}
                              clampBehavior="strict"
                              allowDecimal={false}
                              value={band.max}
                              onChange={(v) =>
                                typeof v === 'number' && setDraft(withMax(definition, index, v))
                              }
                              className={classes.narrow}
                            />
                          </span>
                        ) : (
                          `${band.min}–${band.max}`
                        )}
                      </td>
                      <td>
                        {editable ? (
                          <Select
                            size="xs"
                            className={classes.acceptanceField}
                            aria-label={`${k('acceptance')} ${n}`}
                            allowDeselect={false}
                            value={band.acceptance}
                            data={RA_ACCEPTANCES.map((a) => ({
                              value: a,
                              label: k(`acceptances.${a}`),
                            }))}
                            onChange={(v) =>
                              v &&
                              setDraft(
                                withBand(definition, index, { acceptance: v as RaAcceptance }),
                              )
                            }
                          />
                        ) : (
                          k(`acceptances.${band.acceptance}`)
                        )}
                      </td>
                      <td>
                        {editable ? (
                          <MultiSelect
                            size="xs"
                            className={classes.acceptorField}
                            aria-label={`${k('acceptedBy')} ${n}`}
                            disabled={band.acceptance === 'unacceptable'}
                            value={[...band.acceptedBy]}
                            data={RA_ACCEPTORS.map((r) => ({
                              value: r,
                              label: k(`acceptors.${r}`),
                            }))}
                            onChange={(v) =>
                              setDraft(
                                withBand(definition, index, { acceptedBy: v as RaAcceptor[] }),
                              )
                            }
                          />
                        ) : (
                          acceptorLabel(band.acceptedBy)
                        )}
                      </td>
                      {editable ? (
                        <td>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            aria-label={k('removeBand', { n })}
                            disabled={definition.bands.length <= 2}
                            onClick={() => setDraft(removeBand(definition, index))}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {editable ? (
              <Button
                variant="subtle"
                size="xs"
                mt="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => setDraft(addBand(definition))}
              >
                {k('addBand')}
              </Button>
            ) : null}
            {issues.length > 0 ? (
              <Alert color="red" icon={<IconAlertCircle size={16} />} role="alert" mt="sm">
                <ul className={classes.issues}>
                  {issues.map((issue) => (
                    <li key={`${issue.code}-${issue.band}`}>
                      {k(`issues.${issue.code}`, { n: issue.band + 1 })}
                    </li>
                  ))}
                </ul>
              </Alert>
            ) : null}
          </section>
          <section aria-label={k('preview')}>
            <h3 className={`${settingsClasses.sectionTitle} ${classes.title}`}>{k('preview')}</h3>
            <MatrixPreview definition={definition} />
          </section>
        </div>
      ) : null}

      {notice ? (
        <Alert
          color={notice.tone === 'success' ? 'green' : 'red'}
          role={notice.tone === 'success' ? 'status' : 'alert'}
          mt="sm"
        >
          {notice.text}
        </Alert>
      ) : null}

      {editable && profile && latest && definition ? (
        <>
          <Textarea
            mt="md"
            label={k('reason')}
            description={k('reasonHint')}
            value={reason}
            maxLength={2000}
            autosize
            minRows={2}
            onChange={(e) => setReason(e.currentTarget.value)}
          />
          <CardFooter>
            <Button
              loading={busy}
              disabled={!changed || issues.length > 0 || reason.trim().length < 5}
              onClick={() =>
                void run(
                  () => onPublish(profile.id, latest.version, definition, reason),
                  k('published', { version: latest.version + 1 }),
                )
              }
            >
              {k('publish', { version: latest.version + 1 })}
            </Button>
          </CardFooter>
        </>
      ) : null}

      {profile ? (
        <section className={classes.history} aria-label={k('history')}>
          <h3 className={`${settingsClasses.sectionTitle} ${classes.title}`}>{k('history')}</h3>
          <ol>
            {[...profile.versions].reverse().map((v) => (
              <li key={v.id}>
                <span>
                  {k('historyItem', {
                    version: v.version,
                    at: formatDay(v.publishedAt.slice(0, 10)),
                    by: v.publishedByName ?? 'Felixmate',
                  })}
                </span>
                {v.reason ? <span className={classes.reason}>{v.reason}</span> : null}
              </li>
            ))}
          </ol>
        </section>
      ) : null}
    </Panel>
  );
}

/** Kaynağa bağlı bölüm: okur, seçer, yayımlar, kaydedileni gösterir. */
export function RiskMatrixSettingsSection({ source }: { source: RiskMatrixSettingsSource }) {
  const [settings, setSettings] = useState<RiskMatrixSettings | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` yeniden okutur
  useEffect(() => {
    let live = true;
    setError(false);
    source
      .load()
      .then((value) => {
        if (live) setSettings(value);
      })
      .catch(() => {
        if (live) setError(true);
      });
    return () => {
      live = false;
    };
  }, [source, attempt]);
  const { select, publish } = source;
  return (
    <RiskMatrixSettingsPanel
      settings={settings}
      error={error}
      onRetry={() => setAttempt((n) => n + 1)}
      {...(select
        ? {
            onSelect: async (rowVersion: number | null, profileId: string) => {
              setSettings(await select(rowVersion, profileId));
            },
          }
        : {})}
      {...(publish
        ? {
            onPublish: async (
              profileId: string,
              expectedVersion: number,
              definition: RaMatrixDefinition,
              reason: string,
            ) => {
              setSettings(await publish(profileId, expectedVersion, definition, reason));
            },
          }
        : {})}
    />
  );
}
